import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FormationCard } from '@/components/formations/FormationCard';
import { NewsletterHero } from '@/components/home/NewsletterHero';
import { SEOHead } from '@/components/SEO/SEOHead';
import { useFormations } from '@/hooks/useSupabase';
import { Badge } from '@/components/ui/badge';
import { Award, BookOpen, Users, Target, FileCheck, HeadphonesIcon, GraduationCap } from 'lucide-react';


export function FormationsPage() {
  const { t, i18n } = useTranslation('formations');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  const [selectedCategory, setSelectedCategory] = useState('');
  const { data: formations, isLoading } = useFormations();

  // Filter formations by category
  const filteredFormations = formations?.filter((formation) =>
    selectedCategory ? formation.category === selectedCategory : true
  );

  // Extract unique categories
  const categories = [t('filters.all'), ...new Set(formations?.map((f) => f.category) || [])];

  return (
    <div>
      <SEOHead
        title={t('seo.title', 'Formations Agriculture Numerique')}
        description={t('seo.description', 'Formations certifiees QUALIOPI en agriculture numerique')}
        path="/formations"
      />
      {/* Hero */}
      <section className="bg-[#F06C6E] py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Left: Title & Description */}
            <div className="lg:col-span-2">
              <h1 className="text-4xl md:text-5xl font-heading font-extrabold mb-4 text-white">
                {t('hero.title')}
              </h1>
              <p className="text-lg text-white/90">
                {t('hero.subtitle')}
              </p>
            </div>

            {/* Right: 3 Cards - Grid 2x2 */}
            <div className="grid grid-cols-2 gap-3">
              {/* Card 1: QUALIOPI */}
              <div className="bg-white rounded-xl p-3 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-10 h-10 rounded-lg bg-aspexit-red/10 flex items-center justify-center flex-shrink-0 p-1.5">
                    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                      {/* Badge QUALIOPI stylisé */}
                      <circle cx="50" cy="50" r="38" fill="#F06C6E" opacity="0.15"/>
                      <circle cx="50" cy="50" r="38" fill="none" stroke="#F06C6E" strokeWidth="3"/>
                      {/* Étoile centrale */}
                      <path d="M 50 25 L 54 42 L 71 42 L 58 52 L 63 69 L 50 58 L 37 69 L 42 52 L 29 42 L 46 42 Z"
                            fill="#F06C6E"/>
                      {/* Check marks autour */}
                      <path d="M 30 32 L 34 36 L 40 28" fill="none" stroke="#F06C6E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M 60 28 L 64 32 L 70 24" fill="none" stroke="#F06C6E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M 72 58 L 76 62 L 82 54" fill="none" stroke="#F06C6E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M 18 58 L 22 62 L 28 54" fill="none" stroke="#F06C6E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="font-bold text-aspexit-black text-xs leading-tight">
                    {t('hero.qualiopiBadge')}
                  </div>
                </div>
              </div>

              {/* Card 2: Eligibles Financement */}
              <div className="bg-white rounded-xl p-3 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-10 h-10 rounded-lg bg-aspexit-red/10 flex items-center justify-center flex-shrink-0 p-1.5">
                    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                      {/* Pièces/billets élégants */}
                      <circle cx="35" cy="40" r="18" fill="#F06C6E" opacity="0.2"/>
                      <circle cx="35" cy="40" r="18" fill="none" stroke="#F06C6E" strokeWidth="2.5"/>
                      <text x="35" y="46" fill="#F06C6E" fontSize="20" fontWeight="bold" textAnchor="middle">€</text>
                      <circle cx="60" cy="55" r="18" fill="#F06C6E" opacity="0.15"/>
                      <circle cx="60" cy="55" r="18" fill="none" stroke="#F06C6E" strokeWidth="2.5"/>
                      <text x="60" y="61" fill="#F06C6E" fontSize="20" fontWeight="bold" textAnchor="middle">€</text>
                      {/* Flèche de circulation */}
                      <path d="M 72 35 L 82 35 L 78 30" fill="none" stroke="#F06C6E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M 82 35 L 82 45" fill="none" stroke="#F06C6E" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="font-bold text-aspexit-black text-xs leading-tight">
                    {t('hero.eligibleFinancing')}
                  </div>
                </div>
              </div>

              {/* Card 3: Groupes Réduits - Spans 2 columns */}
              <div className="bg-white rounded-xl p-3 shadow-md hover:shadow-lg transition-shadow col-span-2">
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-10 h-10 rounded-lg bg-aspexit-red/10 flex items-center justify-center flex-shrink-0 p-1.5">
                    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                      {/* Groupe de 3-4 personnes stylisées */}
                      <circle cx="30" cy="35" r="10" fill="#F06C6E" opacity="0.3"/>
                      <path d="M 20 50 Q 20 45 30 45 Q 40 45 40 50 L 40 60 Q 40 65 30 65 Q 20 65 20 60 Z"
                            fill="#F06C6E" opacity="0.2"/>
                      <circle cx="50" cy="30" r="11" fill="#F06C6E" opacity="0.4"/>
                      <path d="M 39 47 Q 39 42 50 42 Q 61 42 61 47 L 61 60 Q 61 66 50 66 Q 39 66 39 60 Z"
                            fill="#F06C6E" opacity="0.3"/>
                      <circle cx="70" cy="35" r="10" fill="#F06C6E" opacity="0.3"/>
                      <path d="M 60 50 Q 60 45 70 45 Q 80 45 80 50 L 80 60 Q 80 65 70 65 Q 60 65 60 60 Z"
                            fill="#F06C6E" opacity="0.2"/>
                      {/* Badge "3-8" */}
                      <circle cx="75" cy="25" r="8" fill="#F06C6E"/>
                      <text x="75" y="28" fill="white" fontSize="9" fontWeight="bold" textAnchor="middle">3-8</text>
                    </svg>
                  </div>
                  <div className="font-bold text-aspexit-black text-xs leading-tight">
                    {t('hero.smallGroups')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Language Notice - Only for non-French languages */}
      {currentLocale !== 'fr' && (
        <section className="py-6 bg-aspexit-gray-light">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl border-2 border-aspexit-red/20 p-6 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-aspexit-red/10 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 100 100" className="w-7 h-7" xmlns="http://www.w3.org/2000/svg">
                      {/* Globe with language symbols */}
                      <circle cx="50" cy="50" r="35" fill="#F06C6E" opacity="0.15" />
                      <circle cx="50" cy="50" r="35" fill="none" stroke="#F06C6E" strokeWidth="3" />
                      {/* Meridians */}
                      <ellipse cx="50" cy="50" rx="15" ry="35" fill="none" stroke="#F06C6E" strokeWidth="2" />
                      <ellipse cx="50" cy="50" rx="35" ry="15" fill="none" stroke="#F06C6E" strokeWidth="2" />
                      {/* Info icon */}
                      <circle cx="70" cy="30" r="12" fill="#F06C6E" />
                      <text x="70" y="37" fill="white" fontSize="18" fontWeight="bold" textAnchor="middle">i</text>
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-heading font-bold text-aspexit-black mb-3">
                      {t('languageNotice.title')}
                    </h2>
                    <p className="text-sm text-aspexit-gray leading-relaxed">
                      {t('languageNotice.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      {formations && formations.length > 3 && (
        <section className="py-8 bg-white border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <Badge
                  key={category}
                  variant={
                    selectedCategory === '' && category === t('filters.all')
                      ? 'default'
                      : selectedCategory === category
                      ? 'default'
                      : 'outline'
                  }
                  className={`cursor-pointer transition-all ${
                    selectedCategory === '' && category === t('filters.all')
                      ? 'bg-aspexit-red hover:bg-aspexit-red-dark'
                      : selectedCategory === category
                      ? 'bg-aspexit-red hover:bg-aspexit-red-dark'
                      : 'hover:border-aspexit-red hover:text-aspexit-red'
                  }`}
                  onClick={() =>
                    setSelectedCategory(category === t('filters.all') ? '' : category)
                  }
                >
                  {category}
                </Badge>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Formations Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          {isLoading ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 border-4 border-aspexit-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-aspexit-gray">{t('loading')}</p>
            </div>
          ) : filteredFormations && filteredFormations.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredFormations.map((formation) => (
                <FormationCard
                  key={formation.id}
                  slug={formation.slug}
                  title={formation.title}
                  description={formation.description}
                  image={formation.image}
                  duration_hours={formation.duration_hours}
                  duration_days={formation.duration_days}
                  price_ttc={formation.price_ttc}
                  category={formation.category}
                  mode="distanciel"
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl text-aspexit-gray mb-4">
                {t('noResults.title')}
              </p>
              <p className="text-aspexit-gray">
                {t('noResults.subtitle')}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Financing */}
      <section className="py-24 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Main message */}
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-aspexit-black mb-4">
                {t('financing.title')}
                <br />
                <span className="text-aspexit-red">{t('financing.titleHighlight')}</span>
              </h2>

              <p className="text-lg text-aspexit-gray max-w-3xl mx-auto mb-8">
                {t('financing.description')}
              </p>
            </div>

            {/* Financing options */}
            <div className="max-w-2xl mx-auto mb-10">
              <div className="bg-white rounded-xl p-8 border-2 border-aspexit-red/20 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-aspexit-red/10 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 100 100" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg">
                      {/* Handshake moderne - main gauche */}
                      <path d="M 20 45 Q 15 50 15 60 L 15 70 Q 15 75 20 75 L 35 75 Q 38 75 40 72 L 45 65"
                            fill="#F06C6E" opacity="0.2" stroke="#F06C6E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>

                      {/* Handshake moderne - main droite */}
                      <path d="M 80 45 Q 85 50 85 60 L 85 70 Q 85 75 80 75 L 65 75 Q 62 75 60 72 L 55 65"
                            fill="#F06C6E" opacity="0.2" stroke="#F06C6E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>

                      {/* Poignée de main centrale */}
                      <ellipse cx="50" cy="50" rx="18" ry="12" fill="#F06C6E" opacity="0.15"/>
                      <path d="M 32 50 Q 40 45 50 45 Q 60 45 68 50 Q 60 55 50 55 Q 40 55 32 50 Z"
                            fill="#F06C6E" stroke="#F06C6E" strokeWidth="2"/>

                      {/* Doigts stylisés */}
                      <line x1="35" y1="48" x2="35" y2="40" stroke="#F06C6E" strokeWidth="2.5" strokeLinecap="round"/>
                      <line x1="40" y1="46" x2="40" y2="36" stroke="#F06C6E" strokeWidth="2.5" strokeLinecap="round"/>
                      <line x1="60" y1="46" x2="60" y2="36" stroke="#F06C6E" strokeWidth="2.5" strokeLinecap="round"/>
                      <line x1="65" y1="48" x2="65" y2="40" stroke="#F06C6E" strokeWidth="2.5" strokeLinecap="round"/>

                      {/* Étoile d'accord/succès */}
                      <circle cx="50" cy="25" r="8" fill="#F06C6E" opacity="0.3"/>
                      <path d="M 50 20 L 52 24 L 56 24 L 53 27 L 54 31 L 50 28 L 46 31 L 47 27 L 44 24 L 48 24 Z"
                            fill="#F06C6E"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-bold text-aspexit-black mb-3">
                      {t('financing.companies.title')}
                    </h3>
                    <ul className="text-sm text-aspexit-gray space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-aspexit-red mt-1">✓</span>
                        <span dangerouslySetInnerHTML={{ __html: t('financing.companies.opco') }} />
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-aspexit-red mt-1">✓</span>
                        <span>{t('financing.companies.plan')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-aspexit-red mt-1">✓</span>
                        <span>{t('financing.companies.budget')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <a
                href="https://www.maformation.fr/droits/financer-formation-opco-115479"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-aspexit-red border-2 border-aspexit-red rounded-lg font-semibold hover:bg-aspexit-red hover:text-white transition-colors shadow-lg hover:shadow-xl"
              >
                {t('financing.ctaLink')}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <p className="text-sm text-aspexit-gray mt-4" dangerouslySetInnerHTML={{ __html: t('financing.contactText', { interpolation: { escapeValue: false } }).replace('<1>', `<a href="/${currentLocale}/contact" class="text-aspexit-red hover:underline font-medium">`).replace('</1>', '</a>') }} />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Aspexit */}
      <section className="py-24 bg-aspexit-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black mb-4 text-center">
              {t('whyChoose.title')}
            </h2>
            <p className="text-center text-aspexit-gray mb-16 max-w-2xl mx-auto">
              {t('whyChoose.subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Card 1: QUALIOPI */}
              <div className="bg-aspexit-red rounded-2xl p-8 border-2 border-white/20 hover:border-white/40 hover:shadow-xl hover:bg-aspexit-red-dark transition-all duration-300 group">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="white" strokeWidth="3" opacity="0.3"/>
                    <circle cx="50" cy="50" r="35" fill="none" stroke="white" strokeWidth="3" opacity="0.5"/>
                    <path d="M50 25 L55 40 L70 42 L60 52 L63 67 L50 58 L37 67 L40 52 L30 42 L45 40 Z" fill="white" opacity="0.9"/>
                    <path d="M35 50 L45 60 L65 40" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-white mb-3 text-center">
                  {t('whyChoose.items.qualiopi.title')}
                </h3>
                <p className="text-white/85 text-center leading-relaxed">
                  {t('whyChoose.items.qualiopi.description')}
                </p>
              </div>

              {/* Card 2: Expert */}
              <div className="bg-aspexit-red rounded-2xl p-8 border-2 border-white/20 hover:border-white/40 hover:shadow-xl hover:bg-aspexit-red-dark transition-all duration-300 group">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="50" cy="35" r="15" fill="white" opacity="0.3"/>
                    <path d="M30 50 Q30 42 50 42 Q70 42 70 50 L70 70 Q70 75 50 75 Q30 75 30 70 Z" fill="white" opacity="0.2"/>
                    <path d="M25 30 L50 22 L75 30 L50 38 Z" fill="white"/>
                    <rect x="48" y="30" width="4" height="12" fill="white"/>
                    <circle cx="50" cy="42" r="3" fill="white"/>
                    <circle cx="70" cy="60" r="12" fill="white" fillOpacity="0.2" stroke="white" strokeWidth="2"/>
                    <text x="70" y="64" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="system-ui">10+</text>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-white mb-3 text-center">
                  {t('whyChoose.items.expert.title')}
                </h3>
                <p className="text-white/85 text-center leading-relaxed">
                  {t('whyChoose.items.expert.description')}
                </p>
              </div>

              {/* Card 3: Pratique */}
              <div className="bg-aspexit-red rounded-2xl p-8 border-2 border-white/20 hover:border-white/40 hover:shadow-xl hover:bg-aspexit-red-dark transition-all duration-300 group">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <rect x="20" y="30" width="60" height="40" rx="3" fill="none" stroke="white" strokeWidth="3"/>
                    <rect x="15" y="70" width="70" height="3" rx="1.5" fill="white"/>
                    <path d="M50 45 L50 60 M50 50 C50 45 55 42 58 40 M50 50 C50 45 45 42 42 40" stroke="#55BC8C" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
                    <circle cx="47" cy="54" r="2" fill="#55BC8C" opacity="0.9"/>
                    <circle cx="53" cy="52" r="1.5" fill="#55BC8C" opacity="0.9"/>
                    <ellipse cx="50" cy="62" rx="5" ry="2" fill="#55BC8C" opacity="0.6"/>
                    <path d="M65 50 L68 45 L71 48 L74 42" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-white mb-3 text-center">
                  {t('whyChoose.items.practical.title')}
                </h3>
                <p className="text-white/85 text-center leading-relaxed">
                  {t('whyChoose.items.practical.description')}
                </p>
              </div>

              {/* Card 4: Groupes réduits */}
              <div className="bg-aspexit-red rounded-2xl p-8 border-2 border-white/20 hover:border-white/40 hover:shadow-xl hover:bg-aspexit-red-dark transition-all duration-300 group">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="35" cy="35" r="10" fill="white" opacity="0.5"/>
                    <path d="M22 65 Q22 50 35 50 Q48 50 48 65 Z" fill="white" opacity="0.5"/>
                    <circle cx="65" cy="35" r="10" fill="white" opacity="0.6"/>
                    <path d="M52 65 Q52 50 65 50 Q78 50 78 65 Z" fill="white" opacity="0.6"/>
                    <circle cx="50" cy="42" r="11" fill="white" opacity="0.85"/>
                    <path d="M36 70 Q36 54 50 54 Q64 54 64 70 Z" fill="white" opacity="0.85"/>
                    <rect x="70" y="20" width="24" height="14" rx="7" fill="white" fillOpacity="0.25" stroke="white" strokeWidth="2"/>
                    <text x="82" y="30" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="system-ui">3-8</text>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-white mb-3 text-center">
                  {t('whyChoose.items.smallGroups.title')}
                </h3>
                <p className="text-white/85 text-center leading-relaxed">
                  {t('whyChoose.items.smallGroups.description')}
                </p>
              </div>

              {/* Card 5: Support */}
              <div className="bg-aspexit-red rounded-2xl p-8 border-2 border-white/20 hover:border-white/40 hover:shadow-xl hover:bg-aspexit-red-dark transition-all duration-300 group">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <rect x="30" y="15" width="40" height="55" rx="3" fill="white" fillOpacity="0.15" stroke="white" strokeWidth="3"/>
                    <line x1="38" y1="28" x2="62" y2="28" stroke="white" strokeWidth="2"/>
                    <line x1="38" y1="38" x2="62" y2="38" stroke="white" strokeWidth="2"/>
                    <line x1="38" y1="48" x2="58" y2="48" stroke="white" strokeWidth="2"/>
                    <line x1="38" y1="58" x2="55" y2="58" stroke="white" strokeWidth="2"/>
                    <circle cx="65" cy="70" r="12" fill="white" opacity="0.9"/>
                    <path d="M65 62 L65 75 M60 70 L65 75 L70 70" stroke="#F06C6E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-white mb-3 text-center">
                  {t('whyChoose.items.support.title')}
                </h3>
                <p className="text-white/85 text-center leading-relaxed">
                  {t('whyChoose.items.support.description')}
                </p>
              </div>

              {/* Card 6: Suivi */}
              <div className="bg-aspexit-red rounded-2xl p-8 border-2 border-white/20 hover:border-white/40 hover:shadow-xl hover:bg-aspexit-red-dark transition-all duration-300 group">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="50" cy="35" r="14" fill="white" opacity="0.3"/>
                    <path d="M32 50 Q32 43 50 43 Q68 43 68 50 L68 68 Q68 72 50 72 Q32 72 32 68 Z" fill="white" opacity="0.2"/>
                    <path d="M35 35 Q35 25 50 25 Q65 25 65 35" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
                    <rect x="32" y="32" width="6" height="8" rx="3" fill="white"/>
                    <rect x="62" y="32" width="6" height="8" rx="3" fill="white"/>
                    <path d="M65 38 Q72 40 72 45" stroke="white" strokeWidth="2" fill="none"/>
                    <circle cx="72" cy="47" r="2" fill="white"/>
                    <path d="M20 45 L23 48 L28 42" stroke="#55BC8C" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                    <path d="M72 60 L75 63 L80 57" stroke="#55BC8C" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-white mb-3 text-center">
                  {t('whyChoose.items.followUp.title')}
                </h3>
                <p className="text-white/85 text-center leading-relaxed">
                  {t('whyChoose.items.followUp.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterHero />
    </div>
  );
}
